import { cn } from "@/lib/utils";

export type StatusVariant =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "default";

export interface StatusBadgeProps {
  status: string;
  variant?: StatusVariant;
  showDot?: boolean;
  className?: string;
  customColors?: {
    background?: string;
    text?: string;
    dot?: string;
    border?: string;
  };
}

// Status mapping configuration
const statusMap: Record<string, StatusVariant> = {
  // Success states
  delivered: "success",
  active: "success",
  completed: "success",
  approved: "success",
  confirmed: "success",
  accepted: "success",
  published: "success",
  online: "success",
  started: "success",

  // Warning states
  pending: "warning",
  processing: "warning",
  review: "warning",
  waiting: "warning",
  scheduled: "warning",
  draft: "warning",
  closed: "warning",

  // Danger states
  failed: "danger",
  rejected: "danger",
  declined: "danger",
  inactive: "danger",
  cancelled: "danger",
  expired: "danger",
  error: "danger",
  offline: "danger",
  ended: "danger",

  // Info states
  // started: 'info',
  updated: "info",
  modified: "info",
};

const variantStyles: Record<StatusVariant, string> = {
  success: "bg-green-50 text-green-700 border-green-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
  default: "bg-gray-50 text-gray-700 border-gray-200",
};

const dotStyles: Record<StatusVariant, string> = {
  success: "bg-green-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  info: "bg-blue-500",
  default: "bg-gray-500",
};

export function StatusBadge({
  status,
  variant,
  showDot = true,
  className,
  customColors,
}: StatusBadgeProps) {
  // Determine variant from status if not explicitly provided
  const resolvedVariant =
    variant || statusMap[status?.toLowerCase()] || "default";

  // Capitalize first letter of status
  const displayStatus =
    status?.charAt(0)?.toUpperCase() + status?.slice(1)?.toLowerCase() || "N/A";

  // Special handling for 'started' status with custom colors
  const isStartedStatus = status?.toLowerCase() === "started";
  const startedCustomColors = {
    background: "#EAFFE5C2",
    text: "#3E882D",
    border: "#3E882D",
    dot: "#3E882D",
  };

  // Use custom colors if provided, or started status colors, otherwise use variant styles
  const finalCustomColors =
    customColors || (isStartedStatus ? startedCustomColors : null);

  const backgroundClass = finalCustomColors?.background
    ? ""
    : variantStyles[resolvedVariant];
  const dotClass = finalCustomColors?.dot ? "" : dotStyles[resolvedVariant];

  // Prepare inline styles for custom colors
  const customStyle = finalCustomColors
    ? {
        backgroundColor: finalCustomColors.background,
        color: finalCustomColors.text,
        borderColor: finalCustomColors.border,
      }
    : {};

  const dotStyle = finalCustomColors?.dot
    ? {
        backgroundColor: finalCustomColors.dot,
      }
    : {};

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border tracking-wide",
        backgroundClass,
        className
      )}
      style={customStyle}
    >
      {showDot && (
        <span
          className={cn("w-2 h-2 rounded-full", dotClass)}
          style={dotStyle}
        />
      )}
      {displayStatus}
    </span>
  );
}
